import * as React from "react"
import { format, parseISO } from "date-fns"

import { Skeleton } from "@ibada/ui"
import { OverviewRecentRowsTable, type OverviewRecentRowsTableRow } from "@ibada/overview-widgets"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { describeTransaction } from "@/features/finance/lib/transaction-display-label"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import type { MoneyText } from "@/features/finance/model/finance-types"
import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { WidgetWithheldState, useIsWithheld } from "@/features/finance/overview/widgets/widget-withheld-state"

const MAX_ROWS = 6

export interface TransactionRow {
  id: string
  label: string
  date: string
  /** The ISO instant, for a surface that formats the day in the reader's locale. */
  occurredAt?: string
  /** Money: the canonical decimal string from the API — rendered, never computed with. */
  amount: MoneyText
  isIncome: boolean
}

function safeDate(value: string): string {
  try {
    return format(parseISO(value), "d MMM")
  } catch {
    return value
  }
}

/**
 * Recent Transactions — the latest finance transactions for the selected period (period-sensitive). A
 * compact list, not the full ledger; the Transactions page owns filtering/pagination. Money-in rows
 * (income / transfer-in) read positive, money-out (expense / transfer-out) negative.
 */
/**
 * The latest transactions for the overview's period, labelled for people (a transfer names its counterpart and is
 * never called an expense). Shared by the dashboard widget and the phone's finance overview.
 */
export function useRecentFinanceTransactionRows({
  context,
  limit = MAX_ROWS,
  period: periodOverride,
  enabled = true,
}: {
  context?: FinanceSurfaceContext
  limit?: number
  /** A period other than the board's (a board tile's previous-period fallback). */
  period?: { from: string; to: string }
  enabled?: boolean
} = {}) {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const boardPeriod = useFinanceOverviewPeriod()
  const period = periodOverride ?? boardPeriod
  const { useFinanceTransactionRecordPageQuery } = useFinanceFeatureQuery()

  /*
   * The transaction list REFUSES for a branch whose figures are withheld (ADR-0024): a row IS an amount, and
   * a list of percentages of an undisclosed total answers nothing. Asking anyway produced a red "could not
   * load" card with a Retry that could never succeed.
   */
  const withheld = useIsWithheld(context)

  const query = useFinanceTransactionRecordPageQuery(
    enabled && scope.workspaceId && scope.branchId && !withheld
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId,
          dateFrom: period.from,
          dateTo: period.to,
          page: 1,
          limit,
          sortBy: "occurredAt",
          sortOrder: "desc",
        }
      : null,
  )

  const rows = React.useMemo<TransactionRow[]>(() => {
    const items = query.data?.items ?? []
    return items.map((record) => {
      const { label, isIncome } = describeTransaction(record, t)
      return { id: record.id, label, date: safeDate(record.occurredAt), occurredAt: record.occurredAt, amount: record.amount, isIncome }
    })
  }, [query.data, t])

  return { rows, query, withheld }
}

export function RecentTransactionsWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const { rows, query, withheld } = useRecentFinanceTransactionRows({ context })

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.transactions) ? () => go(OverviewRoutes.transactions) : undefined,
  })

  // Withheld is a STATE, not a failure — the boundary only knows loading, error and empty.
  if (withheld) return <WidgetWithheldState context={context} />

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={query.data ? rows : undefined}
      isEmpty={(r) => r.length === 0}
      skeleton={<RowsSkeleton />}
      errorTitle={t("widgets.recentTransactions.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.recentTransactions.empty.title")}
    >
      {(transactionRows) => (
        <OverviewRecentRowsTable
          rows={transactionRows.map((row): OverviewRecentRowsTableRow => ({
            id: row.id,
            dateLabel: row.date,
            label: row.label,
            valueLabel: `${row.isIncome ? "+" : "−"}${formatMoneySafe(row.amount, scope.currency)}`,
            valueTone: row.isIncome ? "income" : "expense",
            // Undefined rather than a no-op: `go` already refuses where it cannot navigate, but a row that
            // still hovers and takes a click while doing nothing is worse than one that plainly does not.
            onSelect: go.canNavigateTo(OverviewRoutes.transactions) ? () => go(OverviewRoutes.transactions) : undefined,
          }))}
        />
      )}
    </WidgetBoundary>
  )
}

function RowsSkeleton() {
  return (
    <div className="space-y-2 py-1">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="flex items-center gap-2.5">
          <Skeleton className="h-3 w-8" />
          <Skeleton className="h-3 flex-1" />
          <Skeleton className="h-3 w-14" />
        </div>
      ))}
    </div>
  )
}
